import { toRaw } from 'vue';
import { getTemplateObjectRecords } from '../flow/designerObjectIds.js';

export function getTemplateVisibilityTargets(templatePages, sourcePage) {
    return (templatePages ?? [])
        .filter(template => template?.obj === 'template_page' &&
            template.haspid !== null && template.haspid !== undefined &&
            Number.isInteger(Number(template.haspid)) &&
            hasRuntimeObjects(template))
        .map(template => ({
            value: String(template.haspid),
            templateId: Number(template.haspid),
            label: `T${template.haspid} — ${template.comment || 'Popup Template'}`,
            template,
        }));
}

export function validateTemplateVisibilityAction(action, resolveTemplateTarget, sourcePageId, { legacy = false } = {}) {
    if (action?.operation !== 'show' && action?.operation !== 'hide') return 'Select Show or Hide';
    if (action?.target?.templateId === null || action?.target?.templateId === undefined ||
        action.target.templateId === '') return 'Select a template';
    const templateId = Number(action?.target?.templateId);
    if (!Number.isInteger(templateId)) return 'Select a template';
    const template = toRaw(resolveTemplateTarget?.(templateId));
    if (!template) return 'Template no longer exists';
    if (legacy) {
        if (template.targetPage === null || template.targetPage === undefined || template.targetPage === '') {
            return 'Template has no runtime target page';
        }
        const targetPageId = Number(template.targetPage);
        if (!Number.isInteger(targetPageId)) return 'Template has no runtime target page';
        if (Number(sourcePageId) !== targetPageId) return 'Template is linked to a different page';
    }
    if (!hasRuntimeObjects(template)) return 'Template has no runtime objects';
    return '';
}

/** Serialize the page-independent Designer action for the new runtime contract. */
export function serializeSemanticTemplateVisibilityAction(action, resolveTemplateTarget) {
    const error = validateTemplateVisibilityAction(action, resolveTemplateTarget);
    if (error) return null;
    const templateId = Number(action.target.templateId);
    return `template.${action.operation}=${templateId}`;
}

/**
 * Serialize one template operation to the current, legacy openHASP local commands.
 *
 * This is deliberately kept behind an explicit legacy name. The persisted
 * Designer action remains the semantic templateVisibility record; this bridge
 * is the only place that expands it into embedded JSONL/property commands.
 * Show reuses templateRef's `jsonl` records to create/update the popup widgets;
 * Hide sets the hidden property on every runtime object in that same mapping.
 */
export function serializeLegacyTemplateVisibilityAction(action, resolveTemplateTarget, context = {}) {
    const error = validateTemplateVisibilityAction(action, resolveTemplateTarget, context.sourcePageId, { legacy: true });
    if (error) return null;

    const templateId = Number(action.target.templateId);
    const template = toRaw(resolveTemplateTarget(templateId));
    const runtimePageId = Number(template.targetPage);
    const records = getTemplateObjectRecords(template);
    if (action.operation === 'hide') {
        return records.map(record => `p${runtimePageId}b${record.id}.hidden=1`);
    }

    const templateStack = context.templateStack ?? [];
    if (templateStack.includes(templateId)) return null;
    const nextStack = [...templateStack, templateId];

    return records.map(record => {
        const runtimeRecord = { ...record, hidden: false };
        const storedActions = runtimeRecord.actions;
        delete runtimeRecord.actions;
        if (Array.isArray(storedActions) && storedActions.length) {
            const resolveRuntimeTarget = (pageId, objectId) => {
                if (Number(pageId) !== runtimePageId && Number(pageId) !== templateId) return null;
                return findTemplateObject(template, objectId);
            };
            const handlers = context.normalizeObjectActions?.(
                { actions: storedActions }, resolveRuntimeTarget,
            ) ?? [];
            const runtimeHandlers = handlers.map(handler => ({
                ...handler,
                actions: (handler.actions ?? []).map(entry =>
                    (Number(entry?.target?.pageId) === templateId ||
                        Number(entry?.target?.pageId) === runtimePageId) &&
                    findTemplateObject(template, entry?.target?.objectId)
                        ? {
                            ...entry,
                            target: {
                                ...entry.target,
                                pageId: runtimePageId,
                                objectId: Number(entry.target.objectId),
                            },
                        }
                        : entry
                ),
            }));
            const result = context.serializeObjectActions?.(runtimeHandlers, {
                ...context,
                sourcePageId: runtimePageId,
                templateStack: nextStack,
                resolveTarget: resolveRuntimeTarget,
            });
            if (Object.keys(result?.action ?? {}).length) runtimeRecord.action = result.action;
        }
        return `jsonl ${JSON.stringify(runtimeRecord)}`;
    });
}

/**
 * Backwards-compatible export for callers that still explicitly request the
 * old device bridge. New Designer code should use the legacy-named function
 * so the semantic/runtime boundary stays visible.
 */
export const serializeTemplateVisibilityAction = serializeLegacyTemplateVisibilityAction;

function hasRuntimeObjects(template) {
    const rawTemplate = toRaw(template);
    return (rawTemplate?.getChildren?.() ?? []).some(child => child?.haspid !== undefined &&
        Number.isInteger(Number(child.haspid)));
}

function findTemplateObject(template, objectId) {
    const pending = [...(toRaw(template)?.getChildren?.() ?? [])];
    while (pending.length) {
        const object = pending.shift();
        if (Number(object?.haspid) === Number(objectId)) return object;
        pending.push(...(object?.getChildren?.() ?? []));
    }
    return null;
}
